import { joinPath } from './machinePath'
import { notifySignedOut } from '../auth/sessionSignal'
/**
 * Browsing a paired machine's directories — cloud half live (fa58f8e), edge kind
 * `fs.browse` (not the `list_dir` I proposed; the edge already implemented theirs,
 * and the kind never crosses this client anyway — we POST a path, that is all).
 *
 * ONE COMMAND, ONE WAIT, THEN LOCAL EXPANSION. homepage rejected "speed up the
 * heartbeat while a browser is open" for a reason kept verbatim: a beat rate coupled
 * to an open tab outlives the tab. So the machine reports a shallow TREE (depth 2,
 * ≤400 entries, clamped cloud-side); descending one level is instant and local, and
 * only crossing the reported boundary issues another command.
 *
 * THE MEASURED SHAPE IS STILL PENDING. homepage will relay field names only after
 * hitting the deployed endpoint — "我不轉述他的描述,只轉述我打出來的東西" — because
 * coding to a description is exactly how avengents-server shipped an edge that took
 * commands and answered nothing. Until then `parseResult` reads the flat provisional
 * shape and MISSES HONESTLY on anything else: "could not read the answer" is about
 * this client; an empty listing would be about someone's machine.
 *
 * Two render obligations from the contract, both server-side reasons:
 *   truncated   a clamped listing that looks complete reads as a smaller folder
 *   denied      an unreadable directory MARKED, never dropped — a folder that
 *               silently vanishes reads as "not there", and the user goes looking
 *               elsewhere for something that exists
 */

import type { CommandState } from './serverList'
import { getCommand } from './serverList'
import { onCommand, streamOpen } from './commandStream'
import { api } from '../transport/http'

/**
 * Make a directory on the machine, inside the one being browsed.
 *
 * NOT a `browse`-style command with its own polling: browsing predates the general
 * relay and had to be a bespoke cloud command. `/fs/mkdir` is an ordinary route on
 * the machine, so this is an ordinary relayed POST — one request, one answer, the
 * machine's own middleware and its own guards.
 *
 * NOT `fsMkdir` from transport/api.ts either, though it names the same route. That
 * one posts a FormData with a raw same-origin `fetch`, which was right when the
 * browser could reach the machine: it cannot now, so that call lands on the router,
 * which has no `/fs/mkdir`. It still serves the file browser and the unmounted
 * NewAgentSheet, and untangling those is a wider job than this button.
 *
 * The machine's side had to learn this shape: `/fs/mkdir` read `request.post()`,
 * which parses form encodings only and returns an EMPTY mapping for the relay's
 * JSON — so it used to answer "missing parent dir" about a request that named one
 * (avengents-server-aws 12589a0).
 *
 * `deviceId` is passed explicitly rather than left to the relay's current target.
 * The picker browses a machine the caller names, and a create aimed at the wrong
 * machine does not show the wrong answer — it makes a folder there.
 *
 * Refusals are thrown with the MACHINE'S OWN WORDS ("already exists", "invalid
 * folder name", "not a directory: …"): `ApiError.message` is the `error` field the
 * route returned, and those sentences are the ones a person can act on.
 */
/* Internal: createFolderPath below is the only caller. Was exported for
 * DirectoryBrowser, deleted 2026-09-02. */
async function createFolder(
  deviceId: string,
  parent: string,
  name: string,
): Promise<void> {
  await api.post<{ created?: string }>('/fs/mkdir', { dir: parent, name }, deviceId)
}

/**
 * `mkdir -p`, built from the one-level route the machine actually has.
 *
 * The edge's `/fs/mkdir` takes `basename(name)` — deliberately, so a name cannot
 * traverse — and refuses a parent that does not exist. Neither is worth changing: the
 * refusal is a real guard, and "create these three levels" is a CLIENT intention that
 * the machine is right to make explicit. So the segments are walked here, one call
 * each, parent first.
 *
 * ALREADY-EXISTS IS NOT A FAILURE on any segment but conceptually the last, and not
 * even there: a person typing `a/b/c` where `a/b` exists means "make what is missing",
 * which is what `-p` means everywhere else. The refusal is swallowed ONLY for that
 * exact message, so a permission error or a file-in-the-way still travels.
 */
export async function createFolderPath(
  deviceId: string,
  base: string,
  segments: readonly string[],
): Promise<void> {
  let parent = base
  for (const seg of segments) {
    if (!seg) continue
    try {
      await createFolder(deviceId, parent, seg)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      // Narrow on purpose. "already exists" is the one refusal that means the caller's
      // goal is met; anything else — denied, not a directory, a file of that name — is
      // a different situation and must not be walked past silently.
      if (!/already exists/i.test(msg)) throw e
    }
    parent = joinPath(parent, seg)
  }
}

export type BrowseNode = {
  name: string
  /**
   * The machine reported it but could not read it (`denied`) or hit an error.
   * Shown disabled and labelled, never hidden. (`error`'s semantics are unverified —
   * homepage's measured home directory triggered neither — but rendering an
   * unverified mark beats dropping an entry the machine chose to report.)
   */
  mark?: 'denied' | 'error'
  /**
   * PRESENCE of this key is the signal, not its truthiness:
   *   children: []      the machine LOOKED — genuinely empty, render as such
   *   key absent        beyond the reported depth — expandable, click issues a command
   * Collapsing the two shows "empty folder" for one nobody looked inside.
   */
  children?: BrowseNode[]
}

export type BrowseResult =
  | { kind: 'entries'; path: string; nodes: BrowseNode[]; truncated: boolean }
  | { kind: 'no-answer' }
  | { kind: 'unreachable'; detail: string }
  /** Historical pre-contract state; unreachable at runtime, still rendered. */
  | { kind: 'unwired' }

/*
 * The FIRST ask goes out almost immediately, then backs off.
 *
 * A fixed 3s interval was my own layer's heartbeat: I inherited "the answer takes ~30s"
 * from the beat below me and picked a interval that was cheap against it. Now that
 * delivery is pushed, the machine finishes a browse in ~90ms — and a fixed interval
 * would spend that entire saving waiting for my own timer. Latency has to be low at
 * every layer or the slowest one sets it, and the slowest one was me.
 *
 * Backing off keeps the cost bounded when the answer genuinely is slow: the first
 * second holds several asks, the fortieth holds one.
 */
const POLL_MS = 120
const POLL_MAX_MS = 2_000
/*
 * How long to wait before calling it unanswered, chosen from what the cloud tells us
 * about the machine rather than from a worst case.
 *
 * `collect_within_s` is 0 when the machine is holding a connection and 30 when the
 * only route left is the old periodic one. Waiting 45s on a connected machine that
 * died means 45s of "Asking…" for an answer that was never coming; waiting less than
 * a full interval on a disconnected one would call a live machine dead. One number
 * cannot be right for both, and we no longer have to guess which we have.
 */
const GIVE_UP_CONNECTED_MS = 12_000
const POLL_GROWTH = 1.6
const GIVE_UP_MS = 45_000 // one beat is ≤30s; past this the machine is not beating

/**
 * One in-flight browse per (device, path), shared.
 *
 * Diagnosing the owner's report, the command store showed every browse arriving in
 * PAIRS — React StrictMode double-runs the mount effect in dev, so each open of the
 * browser issued two identical commands, and the machine did the same walk twice.
 * Sharing the promise dedupes StrictMode, and double-clicks with it. Cleared on
 * settle so a retry after failure is a genuinely new ask.
 */
const inflight = new Map<string, Promise<BrowseResult>>()

export function browseDirectory(
  deviceId: string,
  path: string | null,
  pollMs = POLL_MS,
  giveUpMs?: number,
): Promise<BrowseResult> {
  const key = `${deviceId}|${path ?? ''}`
  const existing = inflight.get(key)
  if (existing) return existing
  const p = browseUncached(deviceId, path, pollMs, giveUpMs).finally(() => inflight.delete(key))
  inflight.set(key, p)
  return p
}

async function browseUncached(
  deviceId: string,
  path: string | null,
  pollMs: number,
  giveUpMs: number | undefined,
): Promise<BrowseResult> {
  let res: Response
  try {
    res = await fetch(`/account/devices/${encodeURIComponent(deviceId)}/browse`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
    })
  } catch (e) {
    return { kind: 'unreachable', detail: String(e) }
  }
  if (res.status === 401) notifySignedOut()
  if (res.status !== 202) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
    // 409 device_not_collecting arrives here: the queue on that device is full of
    // commands nobody has ever collected. homepage's `message` says what to check;
    // prefer it over the bare code.
    return {
      kind: 'unreachable',
      detail: String(body.message ?? body.error ?? `browse answered ${res.status}`),
    }
  }
  const accepted = (await res.json()) as {
    command_id: string
    collect_within_s?: number
  }
  const { command_id } = accepted
  const deadlineMs =
    giveUpMs ?? (accepted.collect_within_s === 0 ? GIVE_UP_CONNECTED_MS : GIVE_UP_MS)

  const since = Date.now()
  let asksThatFailed = 0
  let wait = pollMs

  /*
   * Be TOLD, and keep asking only as a net.
   *
   * The stream carries the change the moment the cloud has it. The loop below stays
   * because an open socket can still miss a frame, and "the stream is up" must never
   * silently become "the answer will come" — that is the shape of a hang. While the
   * socket is open the net checks rarely; with no socket it is the whole mechanism.
   */
  let pushed: CommandState | null = null
  let wake: (() => void) | null = null
  const unsubscribe = onCommand(command_id, (state) => {
    pushed = state
    wake?.()
  })

  try {
    for (;;) {
      await new Promise<void>((r) => {
        wake = r
        const net = streamOpen() ? Math.max(wait, 5_000) : wait
        setTimeout(r, net)
      })
      wake = null
      wait = Math.min(Math.round(wait * POLL_GROWTH), POLL_MAX_MS)

      /*
       * The deadline is checked BEFORE the ask, and nothing below may skip it.
       *
       * This loop used to `continue` on a failed poll — reasonable in isolation, since one
       * missed poll is not a failed browse. But `continue` jumped over the deadline at the
       * bottom, so a poll that KEPT failing (an expired session, a dev server restarting
       * under an open tab, one bad gateway) span here every 3s forever. The promise never
       * settled, the sheet never left "Asking…", and the owner sat in front of it while the
       * cloud had held the answer for a minute. A hang shows nothing at all — strictly worse
       * than the error it was avoiding.
       */
      if (Date.now() - since > deadlineMs) {
        return asksThatFailed > 0
          ? {
              kind: 'unreachable',
              detail: 'lost contact while waiting for the answer',
            }
          : { kind: 'no-answer' }
      }

      let c
      if (pushed) {
        c = pushed
        pushed = null
      } else
        try {
          c = await getCommand(deviceId, command_id)
        } catch {
          asksThatFailed += 1
          continue // one missed poll is not a failed browse; the deadline above still holds
        }
      asksThatFailed = 0
      if (c.state === 'done') return parseResult(c.result)
      if (c.state === 'failed') {
        return {
          kind: 'unreachable',
          detail: c.error ?? 'browse failed with no reason given',
        }
      }
      if (c.state === 'unknown') return { kind: 'no-answer' }
    }
  } finally {
    unsubscribe()
  }
}

/**
 * The MEASURED shape (homepage hit the deployed endpoint, 2026-08-04):
 *
 *   { path, entries: [{name, type: "dir"|"file"|"denied"|"error", children?}],
 *     truncated, depth }
 *
 * `children: []` versus an ABSENT key carry opposite meanings — looked-and-empty
 * versus beyond-the-depth-boundary — so this reads `'children' in entry`, never the
 * value's truthiness. Files are not emitted: the picker picks directories, and a
 * file is neither navigable nor pickable here (deliberate, not dropped-by-accident).
 * An unrecognised `type` fails the WHOLE parse rather than being skipped: a new type
 * is a contract change, and surfacing it beats silently thinning someone's listing.
 */
function parseResult(result: unknown): BrowseResult {
  const r = (result ?? {}) as Record<string, unknown>
  if (!Array.isArray(r.entries)) {
    return {
      kind: 'unreachable',
      detail: 'unrecognised browse result — expected entries[]',
    }
  }
  const nodes = parseEntries(r.entries)
  if (nodes === null) {
    return {
      kind: 'unreachable',
      detail: 'unrecognised browse entry — not rendering a guess',
    }
  }
  return {
    kind: 'entries',
    path: typeof r.path === 'string' && r.path ? r.path : '/',
    nodes,
    truncated: r.truncated === true,
  }
}

function parseEntries(raw: unknown[]): BrowseNode[] | null {
  const out: BrowseNode[] = []
  for (const e of raw) {
    if (!e || typeof e !== 'object') return null
    const o = e as Record<string, unknown>
    if (typeof o.name !== 'string' || !o.name) return null
    if (o.type === 'file') continue
    const node: BrowseNode = { name: o.name }
    if (o.type === 'denied') node.mark = 'denied'
    else if (o.type === 'error') node.mark = 'error'
    else if (o.type !== 'dir') return null
    if ('children' in o) {
      if (!Array.isArray(o.children)) return null
      const kids = parseEntries(o.children)
      if (kids === null) return null
      node.children = kids
    }
    out.push(node)
  }
  return out
}
